import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, BarChart3, Package, Layers } from "lucide-react";

export default function AnalyticsWidgets({ dateFrom, dateTo, canSeeRevenue }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (!dateFrom && !dateTo) params.days = 30;
    api.get("/dashboard/analytics", { params }).then((r) => setData(r.data)).catch(() => setData({ revenue_visible: false, revenue_trend: [], top_distributors: [], category_mix: [], aging_orders: [] }));
  }, [dateFrom, dateTo]);

  if (!data) return <Card className="p-4 border border-slate-200 shadow-none rounded-xl text-sm text-slate-400" data-testid="analytics-loading">Loading analytics…</Card>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="analytics-widgets">
      {canSeeRevenue && data.revenue_visible && (
        <Card className="p-4 border border-slate-200 shadow-none rounded-xl h-[320px] flex flex-col" data-testid="analytics-revenue-trend">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <h3 className="heading text-[14px] font-semibold text-slate-900">Revenue Trend</h3>
            <span className="text-[10.5px] text-slate-500">{data.window?.date_from} → {data.window?.date_to}</span>
          </div>
          {data.revenue_trend.length === 0 ? <div className="flex-1 flex items-center justify-center text-sm text-slate-400">No data in range.</div> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.revenue_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Line type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}

      {canSeeRevenue && data.revenue_visible && (
        <Card className="p-4 border border-slate-200 shadow-none rounded-xl h-[320px] flex flex-col" data-testid="analytics-top-distributors">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-4 w-4 text-blue-600" />
            <h3 className="heading text-[14px] font-semibold text-slate-900">Top Distributors</h3>
            <span className="text-[10.5px] text-slate-500">by revenue · top 10</span>
          </div>
          {data.top_distributors.length === 0 ? <div className="flex-1 flex items-center justify-center text-sm text-slate-400">No data in range.</div> : (
            <div className="overflow-auto">
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                  <tr><th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Distributor</th><th className="px-2 py-1 text-right">Orders</th><th className="px-2 py-1 text-right">Revenue</th></tr>
                </thead>
                <tbody>
                  {data.top_distributors.map((d, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1 text-slate-400 tabular-nums">{i + 1}</td>
                      <td className="px-2 py-1 font-medium text-slate-800 truncate max-w-[200px]">{d.name}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-slate-600">{d.orders}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-slate-900 font-medium">{formatINR(d.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <Card className="p-4 border border-slate-200 shadow-none rounded-xl h-[320px] flex flex-col" data-testid="analytics-category-mix">
        <div className="flex items-center gap-2 mb-2">
          <Layers className="h-4 w-4 text-purple-600" />
          <h3 className="heading text-[14px] font-semibold text-slate-900">Category Mix</h3>
          <span className="text-[10.5px] text-slate-500">by quantity</span>
        </div>
        {data.category_mix.length === 0 ? <div className="flex-1 flex items-center justify-center text-sm text-slate-400">No data in range.</div> : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.category_mix.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="category" tick={{ fontSize: 10 }} width={100} />
              <Tooltip formatter={(v, name) => name === "value" ? formatINR(v) : v} />
              <Bar dataKey="quantity" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card className="p-4 border border-slate-200 shadow-none rounded-xl h-[320px] flex flex-col" data-testid="analytics-aging">
        <div className="flex items-center gap-2 mb-2">
          <Package className="h-4 w-4 text-orange-600" />
          <h3 className="heading text-[14px] font-semibold text-slate-900">Order Aging</h3>
          <span className="text-[10.5px] text-slate-500">stuck duration · by status</span>
        </div>
        {data.aging_orders.length === 0 ? <div className="flex-1 flex items-center justify-center text-sm text-slate-400">No data in range.</div> : (
          <div className="overflow-auto">
            <table className="w-full text-[12px]">
              <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                <tr><th className="px-2 py-1 text-left">Status</th><th className="px-2 py-1 text-right">0-3d</th><th className="px-2 py-1 text-right">4-7d</th><th className="px-2 py-1 text-right">8-14d</th><th className="px-2 py-1 text-right">15+ d</th></tr>
              </thead>
              <tbody>
                {data.aging_orders.map((r) => (
                  <tr key={r.status} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-medium text-slate-800">{r.status}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{r["0-3d"]}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-yellow-700">{r["4-7d"]}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-amber-700">{r["8-14d"]}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-rose-700 font-semibold">{r["15+ d"]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
